/* tslint:disable: member-ordering */
import { Injectable } from '@angular/core';
import { Actions, Effect } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';

import {
  MULTIPLE_REQUESTS,
  USE_MOCK,
  SYNC_ON_INTERRUPT_ATTEMP,
  SYNC_DURING_TEST,
  SHOW_ONLY_WRONG_AND_EMPTY_RESULTS
} from '../../constants';
import { helpers } from '../../helpers';
import { AssessmentActions } from './assessment.actions';
import { AssessmentService } from './assessment.service';
import { UserService } from '../user';
import { UserActions } from '../user';
import { SocketHandler } from "../../services/socket-handler.service";

@Injectable()
export class AssessmentEffects {
  constructor(
    private actions$: Actions,
    private assessmentService: AssessmentService,
    private assessmentActions: AssessmentActions,
    private userService: UserService,
    private socketService: SocketHandler
  ) { }

  @Effect() loggedin$ = this.actions$
    .ofType(UserActions.LOGIN_SUCCESS)
    .map(action => action['payload'])
    .switchMap(({testMetaData, progress, results, watermarkImage}) => {
      let progressToSet = Object.assign({
        sections: {},
        questions: {}
      }, progress);

      let metaDataToSet = Object.assign({}, testMetaData, {
        ricette: Object.assign([],
          testMetaData.ricette
            .map(ricetta => Object.assign({},
              ricetta, { tempoEsecuzione: ricetta.tempoEsecuzione * 60 })
            )),
        watermarkImage: watermarkImage
      });

      let resultsToSet = Object.assign([], results ? results.score : undefined);

		return Observable.merge(
			Observable.of(this.assessmentActions.setProgress(progressToSet)),
			Observable.of(this.assessmentActions.setMetadata(metaDataToSet)),
			Observable.of(this.assessmentActions.setResults(resultsToSet))
		);
  });

  @Effect() updateProgressWithViewTime$ = Observable.merge(
    this.actions$.ofType(AssessmentActions.SET_ANSWER),
    this.actions$.ofType(AssessmentActions.INTERRUPT_ATTEMP),
    this.actions$.ofType(AssessmentActions.FORCE_VIEW_TIME_SYNC),
    this.actions$.ofType(AssessmentActions.REQUEST_NEW_SECTION),
    this.actions$.ofType(AssessmentActions.REQUEST_TERMINATE_TEST))
    .filter((action) => action.type !== AssessmentActions.INTERRUPT_ATTEMP || SYNC_ON_INTERRUPT_ATTEMP)
    .withLatestFrom(this.assessmentService.getProgress$())
    .switchMap(([action, progress]) => {
      let currentViewTime = 0
        , questionsViewTimeMap = {}
        , sectionsViewTimeMap = {}
        , result;

      for (let i in this.assessmentService.sectionsViewTimeMap) {
        if (this.assessmentService.sectionsViewTimeMap.hasOwnProperty(i)) {
          if (progress['sections'] && progress['sections'][i]) {
            currentViewTime = parseInt(progress['sections'][i].viewTime || 0);
            sectionsViewTimeMap[i] = {
              viewTime: Math.max(currentViewTime, parseInt(this.assessmentService.sectionsViewTimeMap[i]))
            };
          } else {
            sectionsViewTimeMap[i] = {
              viewTime: parseInt(this.assessmentService.sectionsViewTimeMap[i])
            };
          }
        }
      }

      for (let j in this.assessmentService.questionsViewTimeMap) {
        if (this.assessmentService.questionsViewTimeMap.hasOwnProperty(j)) {
          if (progress['questions'] && progress['questions'][j]) {
            currentViewTime = parseInt(progress['questions'][j].viewTime || 0);
            questionsViewTimeMap[j] = {
              viewTime: Math.max(currentViewTime, parseInt(this.assessmentService.questionsViewTimeMap[j]))
            };
          } else {
            questionsViewTimeMap[j] = {
              viewTime: parseInt(this.assessmentService.questionsViewTimeMap[j])
            };
          }
        }
      }

      result = helpers.immutAssign({}, progress, {
        sections: sectionsViewTimeMap,
        questions: questionsViewTimeMap,
        sync: (action.type == AssessmentActions.FORCE_VIEW_TIME_SYNC)
      });

      return Observable.of(this.assessmentActions.setProgress(result));
    });

  // user new section request
  @Effect() newSectionBeforeUserRequest$ = this.actions$
    .ofType(AssessmentActions.REQUEST_NEW_SECTION_BEFORE)
    .map(action => action['payload'])
    .withLatestFrom(this.assessmentService.getLoadedRicette$())
    .switchMap(([id_ricetta, loadedRicette]) => {
      if(!loadedRicette[id_ricetta]){
        return Observable.of(this.assessmentActions.setRicettaUserRequested(id_ricetta, true));
      }
      return Observable.empty();
    })

  // section loaded control before start
  @Effect() newSectionBefore$ = this.actions$
    .ofType(AssessmentActions.REQUEST_NEW_SECTION_BEFORE)
    .map(action => action['payload'])
    .switchMap(id_ricetta => {
      return this.assessmentService.getLoadedRicette$()
      .filter(loadedRicette => loadedRicette[id_ricetta])
      .take(1)
      .switchMap((loaded) => {
        return Observable.of(this.assessmentActions.requestNewSection(id_ricetta));
      });
    })

  // start new section loading
  @Effect() startNewSectionLoading$ = this.actions$
    .ofType(AssessmentActions.REQUEST_NEW_SECTION_BEFORE)
    .switchMap(() => {
      // start new section timer
      this.assessmentService.startNewSectionLoadingTimer();
      // save state
      return Observable.of(this.assessmentActions.setNewSectionLoading(true));
    });

  // end new section loading
  @Effect() endNewSectionLoading$ = this.actions$
    .ofType(AssessmentActions.REQUEST_NEW_SECTION_SUCCESS)
    .switchMap(() => {
      // kill new section timer
      this.assessmentService.killNewSectionLoadingTimer();
      // save state
      return Observable.of(this.assessmentActions.setNewSectionLoading(false));
    });


  @Effect() newSection$ = this.actions$
    .ofType(AssessmentActions.REQUEST_NEW_SECTION)
    .map(action => action['payload'])
    .withLatestFrom(<any>Observable.combineLatest(
      this.assessmentService
        .getCurrentSectionProgress$(),
      this.assessmentService
        .getProgress$(),
      this.userService
        .getUser$()))
    .switchMap(([
      newSectionId, [
        sectionProgress,
        {currentQuestionId, currentSectionId},
        {testId, userId}]]) =>
      this.assessmentService.newSectionRequest$(
        userId,
        testId,
        newSectionId,
        currentQuestionId,
        {
          ricettaId: currentSectionId,
          viewTime: parseInt(this.assessmentService.getSectionViewTime(currentSectionId)),
          questions: sectionProgress
        })
        .delay(1000)
        .switchMap(response =>
          Observable.of(this.assessmentActions
            .requestNewSectionSuccess(USE_MOCK
              ? currentSectionId : response.ricettaId))
        )
        .catch(err =>
          Observable.of(this.assessmentActions.requestNewSectionFail(err)))
    );

  @Effect() terminateTest$ = this.actions$
    .ofType(AssessmentActions.REQUEST_TERMINATE_TEST)
    .map(action => action['payload'])
    .withLatestFrom(<any>Observable.combineLatest(
      this.assessmentService
        .getCurrentSectionProgress$(),
      this.assessmentService
        .getProgress$(),
      this.userService
        .getUser$()))
    .switchMap(([, [
      sectionProgress,
      {currentQuestionId, currentSectionId},
      {testId, userId}]]) => this.assessmentService.terminateTestRequest$(
        userId,
        testId,
        currentSectionId,
        currentQuestionId,
        {
          ricettaId: currentSectionId,
          viewTime: parseInt(this.assessmentService.getSectionViewTime(currentSectionId)),
          questions: sectionProgress
        })
        .delay(1000)
        .switchMap((results) =>
          Observable.of(this.assessmentActions.terminateTestSuccess(results)))
        .catch(err =>
          Observable.of(this.assessmentActions.terminateTestFail(err)))
    );

  @Effect() updateRicette$ = this.actions$
    .ofType(AssessmentActions.TERMINATE_TEST_SUCCESS)
    .filter(() => SHOW_ONLY_WRONG_AND_EMPTY_RESULTS)
    .map(action => action['payload'])
    .switchMap(({hideMap, progress}) => {
      let firstQuestionId = progress.currentQuestionId
        , firstRicettaId = progress.currentSectionId;

      this.socketService.manuallyDisconnect();

      return Observable.merge(
        Observable.of(this.assessmentActions.updateRicette({
          ricette: hideMap,
          currentQuestionId: firstQuestionId
        })),
        Observable.of(this.assessmentActions.setCurrentSectionId(firstRicettaId)));
    });

  @Effect({ dispatch: false }) startTest$ = this.actions$
    .ofType(AssessmentActions.START_TEST)
    .switchMap(() => {
      this.assessmentService.setStartRequest();
      return Observable.empty();
    });

  @Effect() newSectionSuccess$ = this.actions$
    .ofType(AssessmentActions.REQUEST_NEW_SECTION_SUCCESS)
    .map(action => action['payload'])
    .switchMap(id => Observable.of(
      this.assessmentActions.setCurrentSectionId(id))
    );

  @Effect({ dispatch: false }) setAnswer$ = this.actions$
    .ofType(AssessmentActions.SET_ANSWER)
    .map(action => action['payload'])
    .withLatestFrom(<any>this.userService.getUser$())
    .switchMap(([{ricettaId, questionId, answerId}, {testId, userId}]) => {
      this.assessmentService.setAnswerRequest$(
        userId,
        testId,
        ricettaId,
        questionId,
        answerId,
        this.assessmentService.getQuestionViewTime(questionId),
        this.assessmentService.getSectionViewTime(ricettaId)
      );
      return Observable.empty();
    });

  @Effect({ dispatch: false }) forceViewTimeSync$ = this.actions$
    .ofType(AssessmentActions.FORCE_VIEW_TIME_SYNC)
    .map(action => action['payload'])
    .filter(() => SYNC_DURING_TEST)
    .withLatestFrom(<any>Observable.combineLatest(
      this.assessmentService.getProgress$(),
      this.userService.getUser$())
    )
    .switchMap(([isAuto, [progress, user]]) => {
      let viewTime = this.assessmentService.getSectionViewTime(progress.currentSectionId);
      this.assessmentService.setSectionViewTimeRequest(user.testId, progress.currentSectionId, viewTime, progress, isAuto);
      return Observable.empty();
    });

  @Effect({ dispatch: false }) interruptAttemp$ = Observable.merge(
      this.actions$.ofType(AssessmentActions.INTERRUPT_ATTEMP),
      this.actions$.ofType(AssessmentActions.RESEND_PROGRESS_AFTER_RECONNECT)
    )
    .filter((action) => action.type === AssessmentActions.INTERRUPT_ATTEMP && SYNC_ON_INTERRUPT_ATTEMP)
    .withLatestFrom(<any>Observable.combineLatest(
      this.assessmentService
        .getCurrentSectionProgress$(),
      this.assessmentService
        .getProgress$(),
      this.userService
        .getUser$()))
    .switchMap(([, [
      sectionProgress,
      {currentQuestionId, currentSectionId, sections},
      {testId, userId}]]) => {
      this.assessmentService.setProgressRequest(
        userId,
        testId,
        {
          currentSectionId: currentSectionId,
          currentQuestionId: currentQuestionId,
          questions: sectionProgress,
          sections: {
            [currentSectionId]: sections[currentSectionId]
          }
        }
      );

      return Observable.empty();
    });

  @Effect() setCurrentSection$ = Observable.merge(
      this.actions$.ofType(AssessmentActions.REQUEST_RICETTE_SUCCESS),
      this.actions$.ofType(AssessmentActions.REQUEST_FIRST_AVALIABLE_RICETTA_SUCCESS)
    )
    .map(action => action['payload'])
    .withLatestFrom(this.assessmentService.assessment$)
    .switchMap(([, {progress, metadata}]) => {
      let currentSectionId = progress.currentSectionId;

      if (!currentSectionId || currentSectionId === -1) {
        currentSectionId = metadata.ricette[0].id;
      }

      return Observable.of(this.assessmentActions.setCurrentSectionId(currentSectionId));
    });

  @Effect() setCurrentQuestion$ = this.actions$
    .ofType(AssessmentActions.SET_CURRENT_SECTION_ID)
    .map(action => action['payload'])
    .withLatestFrom(this.assessmentService.getCurrentQuestionId$())
    .mergeMap(([currentSectionId, progressQuestionId]) => this.assessmentService
      .getRicettaById$(currentSectionId)
      .withLatestFrom(<any>this.assessmentService
        .getQuestionMapById$(progressQuestionId)),
    ([, progressQuestionId], [currentSection, questionMap]) => {
      let currentQuestionId = progressQuestionId;

      if (typeof progressQuestionId === 'undefined' || progressQuestionId === -1 ||
        (questionMap && questionMap.ricetta !== currentSection.ricettaId)) {
        if (currentSection.turni[0].testoRiferimento &&
          !currentSection.turni[0].testoRiferimento.text) {
          currentQuestionId = currentSection.turni[0].testoRiferimento.id;
        } else {
          if (currentSection.turni[0].domande[0].hide) {
            currentQuestionId = currentSection.firstQuestion;
          } else {
            currentQuestionId = currentSection.turni[0].domande[0].id;
          }
        }
      }

      return currentQuestionId;
    })
    .switchMap(currentQuestionId =>
      Observable.of(this.assessmentActions.setCurrentQuestionId(currentQuestionId)));

  @Effect() setQuestionsMap$ = this.actions$
    .ofType(AssessmentActions.SET_RICETTA)
    .map(action => action['payload'])
    .switchMap(({turni, ricettaId}) => {
      let currentTurno, currentDomande, currentDomanda;
      let questionsMap = {};

      for (let i = 0; i < turni.length; i++) {
        currentTurno = turni[i];
        currentDomande = currentTurno.domande;

        if (typeof currentTurno.testoRiferimento !== 'undefined') {
          questionsMap[currentTurno.testoRiferimento.id] = {
            ricetta: ricettaId,
            turno: i,
            domanda: 'T',
			refresh_enabled: true
          };
        }

        for (let j = 0; j < currentDomande.length; j++) {
          currentDomanda = currentDomande[j];
          questionsMap[currentDomanda.id] = {
            ricetta: ricettaId,
            turno: i,
            domanda: j,
			refresh_enabled: true
          };
        }
      }

      return Observable.merge(
        Observable.of(this.assessmentActions.setQuestionsMap(questionsMap)));
    });

  // NOTE: to make it synchronous, remove setRicette from setQueue$
  // and convert this listener to SET_QUEUE
  @Effect() setRicettaRecursive$ = Observable.merge(
      this.actions$.ofType(AssessmentActions.SET_RICETTA),
      this.actions$.ofType(AssessmentActions.START_PROCESS_QUEUE)
    )
    .filter(() => MULTIPLE_REQUESTS)
    .map(action => action['payload'])
    .withLatestFrom(<any>this.assessmentService.getQueue$())
    .withLatestFrom(<any>this.assessmentService.isMRFirstAvailableSectionLoaded$())
    .withLatestFrom(<any>this.assessmentService.getProgress$())
    .withLatestFrom(<any>this.assessmentService.getMetadata$())
    .switchMap(([[[[, queue], isMRFirstAvailable], progress], metadata]) => {
      let tempoEsecuzioneGlobale = 0;
      metadata.ricette.forEach(r => {
        tempoEsecuzioneGlobale += r.tempoEsecuzione;
      });
      return queue && queue.list.length > 0
        ? Observable.merge(
          Observable.of(this.assessmentActions.setQueue(Object.assign({}, queue, {
            list: queue.list.slice(1, queue.list.length)
          }))),
          this.assessmentService
            .ricettaDetailsRequest$(queue.userId, queue.testId, queue.list[0].id, helpers.getLoadNextRicettaDelay(queue, metadata, progress, isMRFirstAvailable), tempoEsecuzioneGlobale)
            .map(res => res['payload'])
			      .withLatestFrom(this.assessmentService.getSectionsProgress$())
            .switchMap(([{turni, hide, firstQuestion, ricettaId, images}, sectionsProgress]) => {
              const currentSection = sectionsProgress[ricettaId];
              let returnObservables;
              this.assessmentService.addImagesToMap(ricettaId, images);

              returnObservables = Observable.of(this.assessmentActions.setRicetta({
                turni: this.assessmentService.getTurniWithB64ToText(turni),
                hide: hide,
                firstQuestion: firstQuestion,
                ricettaId: ricettaId
              }));

              if (!isMRFirstAvailable && (typeof currentSection === 'undefined' || !currentSection.completed)) {
                returnObservables = Observable.merge(
                  Observable.of(this.assessmentActions.requestFirstAvailableRicettaSuccess()),
                  returnObservables
                );
              }

              return returnObservables;
            })
        )
        : Observable.of(this.assessmentActions.requestRicetteSuccess());
    });

  @Effect() initRicettaLoaded$ = this.actions$
    .ofType(AssessmentActions.SET_METADATA)
    .filter(() => MULTIPLE_REQUESTS)
    .map(action => action['payload'])
    .switchMap(({idUtentePrenotato, idTest, ricette}) => {
      let loaded$ = [];
      ricette.forEach(r => {
        loaded$.push(Observable.of(this.assessmentActions.setRicettaLoaded(r.id, false)));
      });
      return Observable.merge(...loaded$);
    });

  @Effect() setRicettaLoaded$ = this.actions$
    .ofType(AssessmentActions.SET_RICETTA)
    .filter(() => MULTIPLE_REQUESTS)
    .map(action => action['payload'])
    .switchMap(({ricettaId}) => {
      return Observable.of(this.assessmentActions.setRicettaLoaded(ricettaId, true));
    });

  @Effect() setQueue$ = this.actions$
    .ofType(AssessmentActions.SET_METADATA)
    .map(action => action['payload'])
    .switchMap(({idUtentePrenotato, idTest, ricette}) => MULTIPLE_REQUESTS
      ? Observable.merge(
          Observable.of(this.assessmentActions.setQueue({
              userId: idUtentePrenotato,
              testId: idTest,
              list: ricette //ricette.slice(1, ricette.length)
            })
          ),
          Observable.of(this.assessmentActions.startProcessQueue())

        /*,
          this.assessmentService
            .ricettaDetailsRequest$(idUtentePrenotato, idTest, ricette[0].id)
            .map(res => res['payload'])
            .withLatestFrom(this.assessmentService.getSectionsProgress$())
            .switchMap(([{turni, hide, firstQuestion, ricettaId, images}, sectionsProgress]) => {
              const currentSection = sectionsProgress[ricettaId];
              let returnObservables;
              this.assessmentService.addImagesToMap(ricettaId, images);

              returnObservables = Observable.of(this.assessmentActions.setRicetta({
                turni: this.assessmentService.getTurniWithB64ToText(turni),
                hide: hide,
                firstQuestion: firstQuestion,
                ricettaId: ricettaId
              }));

              if (typeof currentSection === 'undefined' || !currentSection.completed) {
                returnObservables = Observable.merge(
                  returnObservables,
                  Observable.of(this.assessmentActions.requestFirstAvailableRicettaSuccess())
                );
              }

              return returnObservables;
            })
            */
      )
      : this.assessmentService
        .ricetteDetailsRequest$(idUtentePrenotato, idTest, ricette.map(r => r.id))
        .switchMap(response => {
          let actions = []
            , currentRicetta;

          for (let i in response) {
            if (response.hasOwnProperty(i)) {
              currentRicetta = response[i];
              this.assessmentService.imageFormat = currentRicetta.imageFormat;
              this.assessmentService.addImagesToMap(i, currentRicetta.images);

              actions.push(Observable.of(
                this.assessmentActions.setRicetta({
                  turni: this.assessmentService.getTurniWithB64ToText(currentRicetta.turni),
                  hide: currentRicetta.hide,
                  firstQuestion: currentRicetta.firstQuestion,
                  ricettaId: i
                }))
              );
            }
          }

          actions.push(Observable.of(
            this.assessmentActions.requestRicetteSuccess())
          );

          return Observable.merge(...actions);
        })
        .catch(err =>
          Observable.of(this.assessmentActions.requestRicetteFail(err))));

  @Effect({ dispatch: false }) sendLogRequest$ = this.actions$
    .ofType(AssessmentActions.SEND_LOG)
    .withLatestFrom(<any>Observable.combineLatest(
      this.assessmentService
        .getCurrentSectionProgress$(),
      this.assessmentService
        .getProgress$(),
      this.userService
        .getUser$(),
	  this.assessmentService.getQuestionsMap$()))
    .switchMap(([, [
      sectionProgress,
      {currentQuestionId, currentSectionId, sections},
      {testId, userId},
	  questionsMap]]) => {
	  const currentQuestionMap = questionsMap[currentQuestionId];
	  const imageType = currentQuestionMap.domanda === 'T' ? 'cove_text' : null;
      this.assessmentService.sendLogRequest(
        userId,
        testId,
        {
          currentSectionViewTime: this.assessmentService.getSectionViewTime(currentSectionId),
          currentSectionId: currentSectionId,
          currentQuestionId: currentQuestionId,
          questions: sectionProgress,
          sections: {
            [currentSectionId]: sections[currentSectionId]
          }
        },
		imageType
      );

      return Observable.empty();
    });

  // sync status ok
  @Effect() setSyncStatusOk$ = this.actions$
    .ofType(AssessmentActions.SET_SECTION_VIEW_TIME_SUCCESS)
    .switchMap(() => Observable.of(this.assessmentActions.setSyncStatus('ok')));

  // sync status ko
  @Effect() setSyncStatusKo$ = this.actions$
    .ofType(AssessmentActions.SET_SECTION_VIEW_TIME_FAIL)
    .switchMap(() => Observable.of(this.assessmentActions.setSyncStatus('ko')));

  // first (after login) update section view time
  @Effect() updateSectionViewTime$ = this.actions$
    .ofType(AssessmentActions.SET_PROGRESS)
    .map(action => action['payload'])
    .take(1)
    .switchMap((progress) => {
      if(progress['sections']){
        Object.keys(progress['sections']).forEach(sectionId => {
          let sectionProgress = progress['sections'][sectionId],
              sectionViewTime = sectionProgress['viewTime'] || 0;
          this.assessmentService.updateSectionViewTime(sectionId, sectionViewTime);
        })
      }
      return Observable.empty();
    });

}



// WEBPACK FOOTER //
// ./src/app/logics/assessment/assessment.effects.ts